import { useEffect, useState } from 'react'
import { useSessionStore } from '~/src/features/account/stores/useSessionStore'
import { shopApi } from '~/src/shared/api/shopApi'
import { sanitizePhoneNumber } from '~/src/shared/lib/formatters'
import { useUiStore } from '~/src/shared/stores/useUiStore'

const INITIAL_FORM = {
  email: '',
  password: '',
  password_confirmation: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  code: '',
}

export function useAccountController() {
  const customer = useSessionStore((state) => state.customer)
  const login = useSessionStore((state) => state.login)
  const register = useSessionStore((state) => state.register)
  const requestOtp = useSessionStore((state) => state.requestOtp)
  const verifyOtp = useSessionStore((state) => state.verifyOtp)
  const showNotice = useUiStore((state) => state.showNotice)
  const [mode, setMode] = useState('sign_up')
  const [authMethod, setAuthMethod] = useState('otp')
  const [otpStep, setOtpStep] = useState('request')
  const [pendingVerification, setPendingVerification] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [checkoutRequests, setCheckoutRequests] = useState([])
  const [loadingCheckoutRequests, setLoadingCheckoutRequests] = useState(false)
  const [form, setForm] = useState(INITIAL_FORM)
  const isOtpVerificationStep = authMethod === 'otp' && (otpStep === 'verify' || pendingVerification !== null)

  useEffect(() => {
    if (!customer) {
      setCheckoutRequests([])
      return
    }

    setLoadingCheckoutRequests(true)
    shopApi.checkoutRequests()
      .then((requests) => setCheckoutRequests(requests))
      .catch((err) => setError(err.message))
      .finally(() => setLoadingCheckoutRequests(false))
  }, [customer])

  function handleModeChange(nextMode) {
    setMode(nextMode)
    setError(null)
    setAuthMethod('otp')
    resetOtpFlow()
  }

  function handleAuthMethodChange(nextMethod) {
    setAuthMethod(nextMethod)
    setError(null)
    resetOtpFlow()
  }

  function handleFieldChange(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: field === 'phone_number' ? sanitizePhoneNumber(value) : value,
    }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      if (authMethod === 'password' && mode === 'sign_in') {
        await login({ email: form.email, password: form.password })
        showNotice('Sesion iniciada')
        return
      }

      if (authMethod === 'password' && mode === 'sign_up') {
        await register(form)
        showNotice('Cuenta creada')
        return
      }

      if (otpStep === 'request') {
        const otpVerification = await requestOtp({
          destination: form.email,
          channel: 'email',
          purpose: mode,
        })

        setAuthMethod('otp')
        setPendingVerification(otpVerification)
        setOtpStep('verify')
        setForm((currentForm) => ({
          ...currentForm,
          code: '',
        }))
        showNotice('Te enviamos un codigo a tu correo')
        return
      }

      await verifyOtp(buildOtpVerificationPayload())
      showNotice(mode === 'sign_up' ? 'Cuenta verificada' : 'Sesion iniciada')
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResendCode() {
    setError(null)
    setSubmitting(true)

    try {
      const otpVerification = await requestOtp({
        destination: form.email,
        channel: 'email',
        purpose: mode,
      })

      setAuthMethod('otp')
      setPendingVerification(otpVerification)
      setOtpStep('verify')
      showNotice('Te enviamos un nuevo codigo')
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  function resetOtpFlow() {
    setOtpStep('request')
    setPendingVerification(null)
    setForm((currentForm) => ({
      ...currentForm,
      code: '',
    }))
  }

  function buildOtpVerificationPayload() {
    const payload = {
      destination: form.email,
      channel: 'email',
      purpose: mode,
      code: form.code,
    }

    if (mode === 'sign_up') {
      payload.customer = {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
        phone_number: form.phone_number,
      }
    }

    return payload
  }

  return {
    customer,
    mode,
    authMethod,
    isOtpVerificationStep,
    otpStep,
    pendingVerification,
    submitting,
    error,
    checkoutRequests,
    loadingCheckoutRequests,
    form,
    actions: {
      handleAuthMethodChange,
      handleFieldChange,
      handleModeChange,
      handleResendCode,
      handleSubmit,
    },
  }
}
